import { ConvertibleEntity } from 'types/ConvertibleEntity'
import ApiBase from './Api'
import { getInvestorAmountInCash, getInvestorAmountInPercentage } from './utils'

const setupDerivedFields = (convertible: ConvertibleEntity) => {
    return {
        ...convertible,
        investors: convertible.investors.map((investor) => ({
            ...investor,
            amountInCash: getInvestorAmountInCash(investor, convertible.loanAmount),
            amountInPercentage: getInvestorAmountInPercentage(investor, convertible.loanAmount),
        })),
    }
}

const CONVERTIBLES_API_PATHS = {
    CONVERTIBLES: '/convertibles',
    CONVERTIBLE: (id: string) => `/convertibles/${id}`,
}

export type ConvertibleInput = {
    shareId: string
}

class ConvertiblesApi extends ApiBase {
    async findAll(): Promise<ConvertibleEntity[]> {
        const response: ConvertibleEntity[] = await this._getRequest(CONVERTIBLES_API_PATHS.CONVERTIBLES)

        return response
    }

    async findByConvertibleId(convertibleId: string, enrichConvertible = true): Promise<ConvertibleEntity | null> {
        if (!convertibleId) {
            return null
        }
        const fetchedConvertible: ConvertibleEntity = await this._getRequest(
            CONVERTIBLES_API_PATHS.CONVERTIBLE(convertibleId),
        )

        if (enrichConvertible) {
            return setupDerivedFields(fetchedConvertible)
        }

        return fetchedConvertible
    }

    async findByCompanyId(companyId: string): Promise<ConvertibleEntity[]> {
        if (!companyId) {
            return []
        }
        const fetchedConvertibles: ConvertibleEntity[] = await this._getRequest(CONVERTIBLES_API_PATHS.CONVERTIBLES, {
            queryParameters: { companyId },
        })

        return fetchedConvertibles
    }

    async create(convertible: ConvertibleInput): Promise<ConvertibleEntity> {
        const response: ConvertibleEntity = await this._postRequest(CONVERTIBLES_API_PATHS.CONVERTIBLES, convertible)

        return response
    }

    async update(convertibleToUpdate: Partial<ConvertibleEntity>): Promise<ConvertibleEntity> {
        const response = (await this._patchRequest(
            CONVERTIBLES_API_PATHS.CONVERTIBLES + '/' + convertibleToUpdate.id,
            convertibleToUpdate,
        )) as ConvertibleEntity

        return response
    }

    async delete(id: string): Promise<{ message: string }> {
        const response = (await this._deleteRequest(CONVERTIBLES_API_PATHS.CONVERTIBLES + '/' + id)) as {
            message: string
        }

        return response
    }
}

export default ConvertiblesApi
